import TextInput from '@common/components/Inputs/TextInput';
import { BoolIcon, EventRepeatIcon, SerialNumberIcon, TextIcon } from '@common/utils/icons/icons';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FieldType } from 'models/enums/fleet-management';
import FleetDataSet from 'models/fleet-management/FleetDataSet';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { selectFleetFieldsConfiguration } from 'store/fleet-management/fleet-management-slice-selectors';

const UNSORTED = 'unsorted';

const DatasetFields: React.FC<{
  dataset: FleetDataSet;
  isEditing: boolean;
  onEdit: (ds: Partial<FleetDataSet>) => void;
}> = ({ dataset, isEditing, onEdit }) => {
  const { t } = useTranslation();

  const allFieldsConfig = useAppSelector((state) => selectFleetFieldsConfiguration(state));
  // A filter-only field (the robot scope) is set among the filters, never picked as a column.
  const fieldsConfig = useMemo(
    () => allFieldsConfig?.filter((f) => !f.filterOnly),
    [allFieldsConfig],
  );

  const [fieldSearch, setFieldSearch] = useState('');

  const fieldSearchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldSearch(event.target.value);
  };

  const groups = Array.from(
    new Set(
      fieldsConfig
        ?.filter((f) => f.model === dataset.model)
        .map((f) => (f.fieldName.split('__').length === 1 ? UNSORTED : f.fieldName.split('__')[0])),
    ),
  );

  const toggleFieldHandler = (field: string) => {
    const prevFields = [...(dataset.fields ?? [])];
    if (prevFields.includes(field)) {
      onEdit({ fields: prevFields.filter((f) => f !== field) });
    } else {
      onEdit({ fields: [...prevFields, field] });
    }
  };

  return (
    <React.Fragment>
      <Stack
        flex={1}
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          overflow: 'hidden',
        })}
        spacing={1}
      >
        <Stack p={2} spacing={1}>
          <Typography variant="h2">{t('title-dataset-fields')}</Typography>
          {isEditing && (
            <TextInput
              sx={{ '& .MuiFilledInput-input': { pt: 1 } }}
              value={fieldSearch}
              onChange={fieldSearchChangeHandler}
              variant="filled"
              placeholder={t('input-label-search')}
            />
          )}
        </Stack>
        <Stack sx={{ height: '100%', overflow: 'auto', p: 2, pt: 0 }}>
          {groups.map((g) => (
            <Box key={g}>
              <Typography variant="overline">{g}</Typography>
              <Stack spacing={0.25}>
                {fieldsConfig
                  ?.filter(
                    (f) =>
                      (isEditing || dataset.fields?.some((df) => f.fieldName === df)) &&
                      (fieldSearch.length === 0 ||
                        f.fieldName.toLowerCase().includes(fieldSearch.toLowerCase()) ||
                        t('fleet-management-field-' + f.fieldName)
                          .toLowerCase()
                          .includes(fieldSearch.toLowerCase())) &&
                      f.model === dataset.model &&
                      (f.fieldName.split('__')[0] === g ||
                        (g === UNSORTED && f.fieldName.split('__').length === 1)),
                  )
                  .map((field) => (
                    <Stack
                      key={field.fieldName}
                      direction="row"
                      spacing={2}
                      onClick={isEditing ? () => toggleFieldHandler(field.fieldName) : undefined}
                      alignItems="center"
                      sx={(theme) => ({
                        height: theme.spacing(5),
                        cursor: isEditing ? 'pointer' : undefined,
                        px: 2,
                        borderRadius: 1,
                        ':hover': {
                          backgroundColor: isEditing ? theme.palette.action.hover : undefined,
                        },
                        backgroundColor:
                          isEditing && dataset.fields?.some((f) => f === field.fieldName)
                            ? theme.palette.action.selected
                            : undefined,
                      })}
                    >
                      <FieldIcon type={field.type} />
                      <Typography>{t('fleet-management-field-' + field.fieldName)}</Typography>
                    </Stack>
                  ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

const FieldIcon: React.FC<{ type: FieldType }> = ({ type }) => {
  switch (type) {
    case FieldType.BOOL:
      return <BoolIcon color="action" />;
    case FieldType.DATE:
      return <EventRepeatIcon color="action" />;
    case FieldType.NUMBER:
      return <SerialNumberIcon color="action" />;
    case FieldType.STRING:
    case FieldType.STRING_LIST:
      break;
    default:
      break;
  }
  return <TextIcon color="action" />;
};

export default DatasetFields;
